<template>
  <div class="min-h-screen" v-if="pageConfig">

    <PageHeader
      v-if="showPageHeader"
      :title="pageConfig.display_name"
      :subtitle="pageConfig.description"
    >
      <template v-if="showLinkedEntitySwitcher" #actions>
        <FilterDropdown
          label="Viewing as"
          :model-value="linkedEntityId"
          :options="linkedEntityOptions"
          sensitive-labels
          @update:model-value="selectLinkedEntity"
        />
      </template>
      <template v-if="showTabBar" #bottom>
        <Page_Tabs
          :tabs="tabList"
          :initial-tab="activeTab"
          no-border
          @select="selectTab"
          class="px-6 pt-4"
        />
      </template>
    </PageHeader>

    <div v-else-if="showTabBar" class="bg-gray-50 border-b border-gray-200">
      <Page_Tabs
        :tabs="tabList"
        :initial-tab="activeTab"
        no-border
        @select="selectTab"
        class="px-6 pt-4"
      />
    </div>

    <!-- Dynamic Sections -->
    <div class="p-6 space-y-4">
      <template v-for="(row, rowIndex) in sectionRows" :key="rowIndex">
        <div :class="row.length === 2 ? 'grid grid-cols-2 gap-6 items-stretch' : ''">
          <!-- selfChrome components provide their own card/border — render bare -->
          <template v-for="section in row" :key="section.id">
            <div v-if="sectionProvidesChrome(section)" class="flex flex-col">
              <component
                :is="getComponent(section.component)"
                :key="`${section.id}:${linkedEntityId || ''}`"
                v-bind="processSettings(section.settings)"
                :sectionId="section.id"
                :expanded="canExpand(section.component) ? !!sectionExpandedStates[section.id] : true"
                :fullWidth="!!sectionsAloneInRow[section.id]"
                class="h-full"
              />
            </div>
            <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
              <ComponentHeader
                :title="section.title"
              >
                <button
                  v-if="canExpand(section.component)"
                  @click.stop="toggleSectionExpand(section.id)"
                  class="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  :title="sectionExpandedStates[section.id] ? 'Collapse to compact view' : 'Expand to full view'"
                >
                  <svg v-if="!sectionExpandedStates[section.id]" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                </button>
              </ComponentHeader>
              <div class="p-4 flex-1">
                <component
                  :is="getComponent(section.component)"
                  :key="`${section.id}:${linkedEntityId || ''}`"
                  v-bind="processSettings(section.settings)"
                  :sectionId="section.id"
                  :expanded="canExpand(section.component) ? !!sectionExpandedStates[section.id] : true"
                  :fullWidth="!!sectionsAloneInRow[section.id]"
                  class="h-full"
                  :class="section.component === 'UserInfoCard' ? '' : 'rounded-lg'"
                />
              </div>
            </div>
          </template>
        </div>
      </template>

    </div>
  </div>

  <!-- Loading State -->
  <div v-else class="min-h-screen flex">
    <LoadingIndicator message="Loading page configuration..." />
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import spiralDots from '@/assets/spiral_dots.svg'
import { COMPONENT_REGISTRY, getComponent, getComponentMeta, canComponentExpand } from '@/config/configurableComponentRegistry'
import { DEFAULT_SECTION_TAB, sectionTab, visibleSections as userVisibleSections } from '@/utils/pageSections'
import PageHeader from './widgetcomponents/library/PageHeader.vue';
import ComponentHeader from './widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from './widgetcomponents/library/LoadingIndicator.vue';
import FilterDropdown from './widgetcomponents/library/FilterDropdown.vue';
import Page_Tabs from './widgetcomponents/Page_Tabs.vue';
import { z } from 'zod'
import { BrowserStore } from '@/utils/BrowserStore'

const LINKED_ENTITY_SCHEMA = z.object({ entityId: z.number().nullable().default(null) })


export default {
  name: 'ConfigurablePage',
  components: {
    PageHeader,
    ComponentHeader,
    LoadingIndicator,
    FilterDropdown,
    Page_Tabs,
    // Dynamically register all components from registry
    ...Object.entries(COMPONENT_REGISTRY).reduce((acc, [key, value]) => {
      acc[key] = value.component
      return acc
    }, {})
  },
  props: {
    pageIdentifier: {
      type: String,
      required: true
    },
    context: {
      type: Object,
      default: () => ({})
    },
    showPageHeader: {
      type: Boolean,
      default: true
    },
    tab: {
      type: String,
      default: null
    },
    defaultTab: {
      type: String,
      default: DEFAULT_SECTION_TAB
    },
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      pageConfig: null,
      backgroundImage: spiralDots,
      showConfigModal: false,
      localLayoutConfig: null,  // For user customizations
      sectionExpandedStates: {},  // Track which sections are expanded
      activeTab: this.tab || this.defaultTab,
      linkedEntities: [],
      linkedEntityId: null
    }
  },
  computed: {
    ...mapGetters('auth', ['current_user', 'isAuthenticated']),
    ...mapGetters('tenant', ['isTenantAdmin', 'current_tenant_roles', 'current_tenant_slug']),

    showLinkedEntitySwitcher() {
      return !!this.pageConfig?.uses_linked_entity && this.linkedEntities.length > 1
    },

    linkedEntityOptions() {
      return this.linkedEntities.map(e => ({ value: e.id, label: e.type_name ? `${e.type_name} · ${e.name}` : e.name }))
    },

    canConfigure() {
      // Users can always customize their own view
      return this.isAuthenticated
    },

    effectiveLayoutConfig() {
      // Use local changes if they exist, otherwise use page config
      return this.localLayoutConfig || this.pageConfig?.layout_config || {}
    },

    allVisibleSections() {
      return userVisibleSections(this.effectiveLayoutConfig, this.current_tenant_roles)
    },

    tabList() {
      const seen = new Set()
      const tabs = []
      for (const section of this.allVisibleSections) {
        const tab = sectionTab(section, this.defaultTab)
        if (!seen.has(tab)) {
          seen.add(tab)
          tabs.push(tab)
        }
      }
      if (!seen.has(this.defaultTab)) {
        tabs.unshift(this.defaultTab)
      }
      return tabs
    },

    showTabBar() {
      return !this.tab && this.tabList.length > 1
    },

    visibleSections() {
      const tab = this.tab || this.activeTab
      return this.allVisibleSections.filter(
        s => sectionTab(s, this.defaultTab) === tab
      )
    },

    hiddenSections() {
      if (!this.effectiveLayoutConfig.sections) return []
      return this.effectiveLayoutConfig.sections
        .filter(s => s.visible === false)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    },

    sectionRows() {
      // Group consecutive half-width sections into pairs
      // Full-width sections get their own row
      // Expanded half-width sections are treated as full-width
      const rows = []
      let currentHalfRow = []

      for (const section of this.visibleSections) {
        const isExpanded = this.sectionExpandedStates[section.id]
        const effectiveWidth = (section.width === 'half' && !isExpanded) ? 'half' : 'full'

        if (effectiveWidth === 'half') {
          currentHalfRow.push(section)
          if (currentHalfRow.length === 2) {
            rows.push(currentHalfRow)
            currentHalfRow = []
          }
        } else {
          // Full width, unspecified, or expanded half-width
          if (currentHalfRow.length > 0) {
            rows.push(currentHalfRow)
            currentHalfRow = []
          }
          rows.push([section])
        }
      }
      // Don't forget trailing half-width section
      if (currentHalfRow.length > 0) {
        rows.push(currentHalfRow)
      }
      return rows
    },

    // Track which sections are alone in their row (for responsive rendering)
    sectionsAloneInRow() {
      const alone = {}
      for (const row of this.sectionRows) {
        if (row.length === 1 && row[0].width === 'half') {
          alone[row[0].id] = true
        }
      }
      return alone
    },

    tabStorageKey() {
      return `configurable_page_tab_${this.pageIdentifier}`
    }
  },
  async mounted() {
    if (this.config) {
      this.applyPageConfig(this.config)
    } else {
      await this.fetchPageConfig()
    }
  },
  methods: {
    applyPageConfig(pageConfig) {
      this.pageConfig = pageConfig
      this.linkedEntities = pageConfig.linked_entities || []
      this.linkedEntityId = pageConfig.linked_entity_id ?? null
      this.localLayoutConfig = JSON.parse(JSON.stringify(pageConfig.layout_config))
      this.restoreActiveTab()
      this.$nextTick(() => this.loadExpandedStates())
    },

    linkedEntityStore() {
      const slug = this.current_tenant_slug
      const userId = this.current_user?.id
      if (!slug || !userId) return null
      return new BrowserStore(`configurable_page_linked_entity:${slug}:${userId}`, LINKED_ENTITY_SCHEMA)
    },

    async selectLinkedEntity(entityId) {
      if (entityId === this.linkedEntityId) return
      this.linkedEntityStore()?.save({ entityId })
      await this.fetchPageConfig()
    },

    async fetchPageConfig() {
      try {
        const saved = this.linkedEntityStore()?.load().entityId
        const response = await axios.get(
          `/api/page-configurations/by-identifier/${this.pageIdentifier}/`,
          { params: saved ? { linked_entity_id: saved } : {} }
        )
        this.applyPageConfig(response.data)
      } catch (error) {
        console.error('Error fetching page config:', error)
        this.$router.push({ name: 'NotFound' })
      }
    },

    getComponent(componentName) {
      // Use the registry to get component
      return getComponent(componentName) ? componentName : null
    },

    processSettings(settings) {
      // Replace dynamic placeholders like CURRENT_USER_ID
      const processed = JSON.parse(JSON.stringify(settings))

      const replacePlaceholders = (obj) => {
        for (let key in obj) {
          if (typeof obj[key] === 'string' && obj[key] === 'CURRENT_USER_ID') {
            obj[key] = this.current_user.id
          } else if (typeof obj[key] === 'string' && obj[key] === 'LINKED_ENTITY_ID') {
            obj[key] = this.linkedEntityId
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            replacePlaceholders(obj[key])
          }
        }
      }

      replacePlaceholders(processed)
      return processed
    },

    selectTab(tab) {
      this.activeTab = tab
      localStorage.setItem(this.tabStorageKey, tab)
    },

    restoreActiveTab() {
      if (this.tab) return
      const stored = localStorage.getItem(this.tabStorageKey)
      if (stored && this.tabList.includes(stored)) {
        this.activeTab = stored
      } else {
        this.activeTab = this.tabList[0] || this.defaultTab
      }
    },

    async toggleSectionVisibility(sectionId) {
      // Find and toggle the section
      const section = this.localLayoutConfig.sections.find(s => s.id === sectionId)
      if (section) {
        section.visible = !section.visible
        // Save to backend
        await this.saveUserConfig()
      }
    },

    async saveUserConfig() {
      try {
        await axios.post('/api/page-configurations/save-user-config/', {
          page_identifier: this.pageIdentifier,
          layout_config: this.localLayoutConfig
        })
        this.$emit('config-saved')
      } catch (error) {
        console.error('Error saving user config:', error)
        alert('Failed to save configuration. Please try again.')
      }
    },

    canExpand(componentName) {
      return canComponentExpand(componentName)
    },

    sectionProvidesChrome(section) {
      if (section.settings?.self_chrome != null) return section.settings.self_chrome
      const defaults = getComponentMeta(section.component)?.defaultSettings
      return defaults?.self_chrome === true
    },

    toggleSectionExpand(sectionId) {
      const currentState = !!this.sectionExpandedStates[sectionId]
      this.sectionExpandedStates[sectionId] = !currentState
      // Persist to localStorage
      const section = this.visibleSections.find(s => s.id === sectionId)
      if (section?.settings?.persist_key) {
        localStorage.setItem(section.settings.persist_key + '_expanded', JSON.stringify(!currentState))
      }
    },

    loadExpandedStates() {
      // Load expanded states from localStorage for all expandable sections
      for (const section of this.allVisibleSections) {
        if (this.canExpand(section.component) && section.settings?.persist_key) {
          const stored = localStorage.getItem(section.settings.persist_key + '_expanded')
          if (stored !== null) {
            this.sectionExpandedStates[section.id] = JSON.parse(stored)
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* Component-specific styles */
</style>
